import {
    AgeRatingDeclaration,
    AgeRatingQuestion,
    AgeRatingQuestionKind,
    AgeRatingQuestionSet,
} from '@/types/ageRating';
import Divider from 'antd/lib/divider';
import Form, { FormInstance } from 'antd/lib/form';
import Modal from 'antd/lib/modal';
import Radio from 'antd/lib/radio';
import Select from 'antd/lib/select';
import Text from 'antd/lib/typography/Text';
import * as React from 'react';

const requiredQuestionRules = [{ required: true, message: 'Answer this question' }];

const areAllAgeRatingQuestionsAnswered = (
    questionSet: AgeRatingQuestionSet | null | undefined,
    declaration: AgeRatingDeclaration | undefined,
): boolean =>
    !!questionSet &&
    questionSet.groups.every((group) =>
        group.questions.every((question) => {
            if (question.key === 'socialMediaAgeRestricted' && declaration?.socialMedia !== true) {
                return true;
            }
            const answer = declaration?.[question.key];
            return answer !== undefined && answer !== null;
        }),
    );

interface AgeRatingQuestionnaireModalProps {
    title: React.ReactNode;
    open: boolean;
    questionSet: AgeRatingQuestionSet;
    initialValues: AgeRatingDeclaration;
    isEditable: boolean;
    saving: boolean;
    description?: React.ReactNode;
    footer?: React.ReactNode;
    onCancel: () => void;
    onSave: (declaration: AgeRatingDeclaration) => void | Promise<void>;
}

export const AgeRatingQuestionnaireModal: React.FC<AgeRatingQuestionnaireModalProps> = ({
    title,
    open,
    questionSet,
    initialValues,
    isEditable,
    saving,
    description,
    footer,
    onCancel,
    onSave,
}) => {
    const [form] = Form.useForm<AgeRatingDeclaration>();
    const watchedAnswers = Form.useWatch([], form) as AgeRatingDeclaration | undefined;
    const areAllQuestionsAnswered = areAllAgeRatingQuestionsAnswered(questionSet, watchedAnswers ?? initialValues);

    const save = async () => {
        const values = await form.validateFields();
        const declaration: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(values)) {
            if (value !== undefined && value !== null) {
                declaration[key] = value;
            }
        }
        if (values.socialMedia === false) {
            declaration.socialMediaAgeRestricted = null;
        }
        await onSave(declaration as AgeRatingDeclaration);
    };

    const syncLatestAnswers = (isOpen: boolean) => {
        if (!isOpen) {
            return;
        }
        form.resetFields();
        form.setFieldsValue(initialValues);
    };

    return (
        <Modal
            title={title}
            open={open}
            width={720}
            onCancel={onCancel}
            onOk={save}
            okText="Save"
            okButtonProps={{ disabled: !isEditable || !areAllQuestionsAnswered, loading: saving }}
            confirmLoading={saving}
            destroyOnClose
            afterOpenChange={syncLatestAnswers}
        >
            {description}
            <Form form={form} layout="vertical" preserve={false} initialValues={initialValues}>
                <AgeRatingQuestionnaireFields questionSet={questionSet} isEditable={isEditable} />
            </Form>
            {footer}
        </Modal>
    );
};

const AgeRatingQuestionnaireFields: React.FC<{
    questionSet: AgeRatingQuestionSet;
    isEditable: boolean;
}> = ({ questionSet, isEditable }) => {
    const form = Form.useFormInstance<AgeRatingDeclaration>();
    const socialMedia = Form.useWatch('socialMedia', form);
    const frequencyOptions = questionSet.frequencyOptions.map((option) => ({
        value: option.value,
        label: option.label,
    }));

    return (
        <>
            {questionSet.groups.map((group, groupIndex) => (
                // An empty title is the "ungrouped" bucket of questions Apple has added
                // but Runway hasn't curated copy/grouping for yet — rendered without a
                // header (the backend appends it last, so it stays at the end).
                <React.Fragment key={group.title || `ungrouped-${groupIndex}`}>
                    {group.title && (
                        <Divider orientation="left" orientationMargin={0}>
                            <Text strong>{group.title}</Text>
                        </Divider>
                    )}
                    {group.questions.map((question) => {
                        if (question.key === 'socialMediaAgeRestricted' && socialMedia !== true) {
                            return null;
                        }
                        return question.kind === AgeRatingQuestionKind.Frequency ?
                                renderFrequencyQuestion(question, frequencyOptions, isEditable)
                            :   renderBooleanQuestion(question, isEditable, form);
                    })}
                </React.Fragment>
            ))}
        </>
    );
};

const labelForQuestion = (question: AgeRatingQuestion): string => {
    if (question.label) {
        return question.label;
    }
    return question.key
        .replace(/([A-Z])/g, ' $1')
        .trim()
        .split(/\s+/)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

const tooltipForQuestion = (question: AgeRatingQuestion): React.ReactNode | undefined => {
    const tooltip = question.tooltip ?? question.helpText;
    return tooltip ? <span style={{ whiteSpace: 'pre-line' }}>{tooltip}</span> : undefined;
};

const renderFrequencyQuestion = (
    question: AgeRatingQuestion,
    options: { value: string; label: string }[],
    isEditable: boolean,
) => (
    <Form.Item
        key={question.key}
        name={question.key}
        label={labelForQuestion(question)}
        extra={question.description ?? question.helpText}
        tooltip={tooltipForQuestion(question)}
        rules={requiredQuestionRules}
    >
        <Select allowClear disabled={!isEditable} placeholder="Not answered" options={options} />
    </Form.Item>
);

const renderBooleanQuestion = (
    question: AgeRatingQuestion,
    isEditable: boolean,
    form: FormInstance<AgeRatingDeclaration>,
) => (
    <Form.Item
        key={question.key}
        name={question.key}
        label={labelForQuestion(question)}
        extra={question.description ?? question.helpText}
        tooltip={tooltipForQuestion(question)}
        rules={requiredQuestionRules}
    >
        <Radio.Group
            disabled={!isEditable}
            onChange={
                question.key === 'socialMedia' ?
                    (event) => {
                        if (event.target.value === false) {
                            form.setFieldValue('socialMediaAgeRestricted', null);
                        }
                    }
                :   undefined
            }
        >
            <Radio value={true}>Yes</Radio>
            <Radio value={false}>No</Radio>
        </Radio.Group>
    </Form.Item>
);
